/**
 * Models for the Global Search API (`/api/search`).
 *
 * Kept separate from the core blog-post model because:
 * - The search endpoint lives at a different path (`/api/search` vs `/api/blog-posts`)
 * - Search results are a read-only, partially populated projection — not the
 *   full blog post shape
 * - `SearchSortBy` includes `'relevance'` which is search-only and must not
 *   pollute the blog-post-specific {@link SortBy} enum
 */

import { ContentFormat, SortOrder } from './enums';
import { PaginationMeta } from './api-response.model';

// ---------------------------------------------------------------------------
// Sort options (search-specific)
// ---------------------------------------------------------------------------

/**
 * Sort fields available for the search endpoint.
 *
 * `'relevance'` is only meaningful when a `query` string is provided;
 * the server falls back to `'createdAt'` for filter-only searches.
 */
export type SearchSortBy = 'relevance' | 'createdAt';

// Re-export for convenience so consumers only need to import from this file.
export { SortOrder as SearchSortOrder };

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

/**
 * Parameters accepted by both `GET /api/search` and `POST /api/search`.
 *
 * All fields are optional; supply at least one to trigger a meaningful search.
 * Sending no fields returns all public posts (paginated).
 */
export interface SearchParams {
  /** Full-text search — searches title, content, excerpt, keywords (max 200 chars). */
  query?: string;
  /** Filter by one or more tags. GET serialises as repeated params (`tags=a&tags=b`). */
  tags?: string[];
  /** Exact MongoDB ObjectId of an author. */
  authorId?: string;
  /** Case-insensitive partial match on author name (max 100 chars). */
  authorQuery?: string;
  /** Exact MongoDB ObjectId of an organisation. */
  orgId?: string;
  /** Case-insensitive partial match on organisation name (max 100 chars). */
  orgQuery?: string;
  /** Exact MongoDB ObjectId of a post type. */
  postTypeId?: string;
  /** Case-insensitive exact post-type name lookup (max 100 chars). */
  postTypeName?: string;
  /** ISO 8601 start date — filters `createdAt ≥ value`. */
  dateFrom?: string;
  /** ISO 8601 end date — filters `createdAt ≤ end-of-day for value`. */
  dateTo?: string;
  /** Page number ≥ 1 (default: 1). */
  page?: number;
  /** Items per page, 1–20 (default: 10, max: 20). */
  limit?: number;
  /** Sort field. Defaults to `'relevance'` when `query` is present, else `'createdAt'`. */
  sortBy?: SearchSortBy;
  /** Sort direction (default: `'desc'`). */
  sortOrder?: SortOrder;
}

// ---------------------------------------------------------------------------
// Embedded sub-documents in search results
// ---------------------------------------------------------------------------

/**
 * Author embedded inside a search result.
 *
 * The field is named `authorId` in the API response (confusingly) but
 * contains the populated author object, or `null` when the author was deleted.
 */
export interface SearchAuthor {
  _id: string;
  name: string;
  photoURL?: string;
}

/**
 * Organisation embedded inside a search result.
 *
 * The field is named `orgId` in the API response but contains the populated
 * organisation object, or `null` when the org was deleted.
 */
export interface SearchOrg {
  _id: string;
  name: string;
  orgCode: string;
}

/**
 * Post type embedded inside a search result.
 *
 * The field is named `postTypeId` in the API response but contains the
 * populated post type object, or `null` when deleted.
 */
export interface SearchPostType {
  _id: string;
  typeName: string;
}

/**
 * Text-match metadata present when a `query` string was supplied.
 */
export interface SearchMeta {
  /** MongoDB text search score. */
  score?: number;
  titleMatches?: number;
  excerptMatches?: number;
  /** Highlighted text snippets from the matching content. */
  highlights?: string[];
}

// ---------------------------------------------------------------------------
// Single search result
// ---------------------------------------------------------------------------

/**
 * One blog post entry in the search result list.
 *
 * This is a read-only projection — it does not include `content` (full body)
 * to keep response payloads small. Use {@link BlogPostService} to fetch a
 * full post.
 */
export interface SearchResult {
  _id: string;
  title: string;
  excerpt?: string;
  slug: string;
  coverImageUrl?: string;
  coverImageAlt?: string;
  tags: string[];
  keywords?: string[];
  contentFormat: ContentFormat;
  /** Search results are always `'public'`. */
  publishStatus: 'public';
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
  /** Populated author, or `null` if the author account no longer exists. */
  authorId: SearchAuthor | null;
  /** Populated organisation, or `null` if the org no longer exists. */
  orgId: SearchOrg | null;
  /** Populated post type, or `null` if the post type was deleted. */
  postTypeId: SearchPostType | null;
  /** Only present when the request included a `query` string. */
  searchMeta?: SearchMeta;
}

// ---------------------------------------------------------------------------
// API response shape
// ---------------------------------------------------------------------------

/**
 * Metadata present in every search response regardless of result count.
 */
export interface SearchResponseMeta {
  /** The `query` string that was searched, if any. */
  query?: string;
  /** Filter keys that were applied (e.g. `['query', 'tags']`). */
  filtersApplied: string[];
  /** Server-side query execution time in milliseconds. */
  executionTimeMs: number;
}

/**
 * The `data` payload of a search API response.
 */
export interface SearchResponseData {
  results: SearchResult[];
  pagination: PaginationMeta;
  meta: SearchResponseMeta;
}

/**
 * Top-level response from `GET /api/search` and `POST /api/search`.
 *
 * `cached: true` indicates the response was served from the 60-second
 * server-side cache — no client-side action needed.
 */
export interface SearchApiResponse {
  success: boolean;
  /** `true` when served from server cache. */
  cached?: boolean;
  data: SearchResponseData;
}

// ---------------------------------------------------------------------------
// Internal service state shape
// ---------------------------------------------------------------------------

/**
 * Signal state managed by {@link PostSearchService}.
 *
 * @internal
 */
export interface SearchState {
  results: SearchResult[];
  pagination: PaginationMeta | null;
  meta: SearchResponseMeta | null;
  loading: boolean;
  error: string | null;
  /** Parameters used in the last executed search — used for pagination. */
  params: SearchParams;
  /** HTTP method used for the last search — preserved across pagination. */
  method: 'get' | 'post';
}
